/**
 * Custom form section for ``call_mapping`` nodes.
 *
 * The generic NodeEditorDrawer renderer can't usefully render
 * ``input_bindings: dict[str, str]`` — it'd produce a JSON textarea
 * keyed by hand, which defeats the point of a typed mapping signature.
 *
 * This editor:
 *   * picks the Mapping (Combobox over /api/admin/mappings),
 *   * once a mapping is selected, fetches its declared inputs /
 *     outputs and renders ONE JSONata input field per declared
 *     input — with a type chip so the user knows what shape the
 *     expression should produce,
 *   * shows the declared outputs read-only so the user knows what
 *     gets merged downstream,
 *   * exposes ``output_path``, ``on_miss`` and ``default_values``
 *     in a compact controls strip below.
 *
 * Save-time validation happens on the backend (each binding compiles
 * as JSONata, missing-binding for declared inputs is rejected by the
 * handler). The UI just shows a yellow chip on unbound declared inputs
 * so the operator sees the gap before saving.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip } from "@/components/ui/tooltip";
import { CodeEditor } from "@/components/canonical/CodeEditor";
import { mappingsApi, type MappingField } from "@/lib/api-mappings";


export interface CallMappingEditorProps {
  /** The node config; we read mapping_id + input_bindings + on_miss
   *  + output_path + default_values and write all of them back via
   *  onChange. */
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}


export function CallMappingEditor({ config, onChange }: CallMappingEditorProps) {
  const mappingId = (config.mapping_id as string) || "";
  const bindings = (config.input_bindings as Record<string, string>) || {};
  const onMiss = (config.on_miss as string) || "fail";
  const outputPath = (config.output_path as string) || "$";
  const outputField = (config.output_field as string | null) ?? null;
  const defaultValues =
    (config.default_values as Record<string, unknown>) || {};

  // ── Mappings list for the picker ──────────────────────────────
  const listQ = useQuery({
    queryKey: ["mappings", "for-picker"],
    queryFn: async () => {
      // Bounded set; pagination not worth the complexity here. If a
      // deployment ever exceeds 100 mappings, swap to async search.
      // page_size is constrained to {25, 50, 100} by PageParams.
      const r = await mappingsApi.list({ page_size: 100 });
      return r.items;
    },
    staleTime: 30_000,
  });

  // ── Detail of the chosen mapping (for inputs / outputs) ───────
  // Skipped while the picker is empty.
  const detailQ = useQuery({
    queryKey: ["mapping", mappingId],
    queryFn: async () => mappingsApi.get(mappingId),
    enabled: !!mappingId,
    staleTime: 30_000,
  });

  const mappingOptions = React.useMemo<ComboboxOption[]>(() => {
    return (listQ.data ?? [])
      .map((m) => ({
        value: m.id,
        label: m.name,
        description: m.enabled
          ? `${m.strategy_kind} • ${m.inputs.length}→${m.outputs.length}`
          : `${m.strategy_kind} • disabled`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [listQ.data]);

  // Pick the new mapping. When the mapping changes, drop bindings for
  // inputs that no longer exist (and keep ones whose name survives —
  // useful when the operator is fixing a typo in mapping_id).
  function pickMapping(newId: string | null) {
    if (!newId) {
      onChange({ ...config, mapping_id: "" });
      return;
    }
    const target = listQ.data?.find((m) => m.id === newId);
    if (!target) {
      onChange({ ...config, mapping_id: newId });
      return;
    }
    const survivingInputNames = new Set(target.inputs.map((f) => f.name));
    const cleaned: Record<string, string> = {};
    for (const [k, v] of Object.entries(bindings)) {
      if (survivingInputNames.has(k)) cleaned[k] = v;
    }
    // Pre-fill empty entries for newly-declared inputs so the form
    // shows a field per input immediately.
    for (const f of target.inputs) {
      if (!(f.name in cleaned)) cleaned[f.name] = "";
    }
    onChange({
      ...config,
      mapping_id: newId,
      input_bindings: cleaned,
    });
  }

  function updateBinding(name: string, expr: string) {
    onChange({
      ...config,
      input_bindings: { ...bindings, [name]: expr },
    });
  }

  function updateScalar<K extends string>(key: K, value: unknown) {
    onChange({ ...config, [key]: value });
  }

  const declaredInputs: MappingField[] = detailQ.data?.inputs ?? [];
  const declaredOutputs: MappingField[] = detailQ.data?.outputs ?? [];
  const unboundCount = declaredInputs.filter(
    (f) => !(bindings[f.name] ?? "").trim(),
  ).length;

  return (
    <div className="space-y-4">
      {/* Mapping picker */}
      <div className="space-y-1.5">
        <Label className="inline-flex items-center gap-1">
          <span className="font-mono text-xs">mapping_id</span>
          <span className="text-destructive">*</span>
          <Tooltip
            content="Which stored Mapping to invoke. The signature is locked once this workflow version is activated."
            side="right"
          >
            <span tabIndex={0} className="cursor-help text-muted-foreground/70 hover:text-foreground">
              <Info className="h-3 w-3" strokeWidth={1.5} />
            </span>
          </Tooltip>
        </Label>
        <Combobox
          options={mappingOptions}
          value={mappingId || null}
          onChange={pickMapping}
          placeholder="Pick a mapping…"
          loading={listQ.isLoading}
          emptyText="No mappings defined. Create one in the Mappings page first."
        />
        {detailQ.data && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>strategy:</span>
            <Badge tone="neutral" className="text-[10px]">
              {detailQ.data.strategy_kind}
            </Badge>
            {!detailQ.data.enabled && (
              <Badge tone="warning" className="text-[10px]">
                disabled — the handler will refuse to run
              </Badge>
            )}
            {detailQ.data.signature_locked && (
              <Badge tone="neutral" className="text-[10px]">
                signature locked
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Input bindings — one row per declared input */}
      {mappingId && detailQ.isLoading && (
        <p className="text-[11px] text-muted-foreground">Loading mapping signature…</p>
      )}

      {detailQ.data && declaredInputs.length === 0 && (
        <Alert tone="info">
          <strong>This mapping has no declared inputs.</strong>
          <p className="mt-1 text-xs">
            It'll be invoked once per item with an empty input dict. Typically
            a constant lookup; double-check this is what you want.
          </p>
        </Alert>
      )}

      {detailQ.data && declaredInputs.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs">
              <span className="font-semibold uppercase tracking-wider text-muted-foreground">
                Input bindings
              </span>
            </Label>
            {unboundCount > 0 && (
              <Badge tone="warning" className="text-[10px]">
                <AlertTriangle className="mr-1 inline h-3 w-3" strokeWidth={2} />
                {unboundCount} unbound
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            One JSONata expression per declared input. Evaluated against the
            current item's <code className="font-mono">.data</code>. Use{" "}
            <code className="font-mono">$.field</code> or{" "}
            <code className="font-mono">$data.field</code> to read.
          </p>
          {declaredInputs.map((f) => {
            const v = bindings[f.name] ?? "";
            const unbound = !v.trim();
            return (
              <div key={f.name} className="space-y-1">
                <Label className="inline-flex items-center gap-2">
                  <span className="font-mono text-xs">{f.name}</span>
                  <Badge tone="neutral" className="text-[9px]">
                    {f.type}
                  </Badge>
                  {f.required && <span className="text-[10px] text-destructive">required</span>}
                  {f.description && (
                    <Tooltip content={f.description} side="right">
                      <span tabIndex={0} className="cursor-help text-muted-foreground/70 hover:text-foreground">
                        <Info className="h-3 w-3" strokeWidth={1.5} />
                      </span>
                    </Tooltip>
                  )}
                </Label>
                <div className="h-16 w-full">
                  <CodeEditor
                    value={v}
                    onChange={(next) => updateBinding(f.name, next ?? "")}
                    language="javascript"
                    height="100%"
                    inline
                  />
                </div>
                {unbound && (
                  <p className="text-[10px] text-amber-600">
                    Empty — the resolver will see <code className="font-mono">{f.name}=null</code>
                    {f.required ? " and likely miss." : "."}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Declared outputs preview — read-only, just context */}
      {detailQ.data && declaredOutputs.length > 0 && (
        <div className="space-y-1.5 border-t border-border/60 pt-3">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Declared outputs (merged into item.data)
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {declaredOutputs.map((f) => (
              <span
                key={f.name}
                className="inline-flex items-center gap-1 rounded border border-border/60 px-1.5 py-0.5"
              >
                <code className="font-mono text-[11px]">{f.name}</code>
                <Badge tone="neutral" className="text-[9px]">
                  {f.type}
                </Badge>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* output_path + on_miss + default_values */}
      <div className="grid grid-cols-2 gap-3 border-t border-border/60 pt-3">
        <div className="space-y-1.5">
          <Label htmlFor="cm-output-path" className="text-xs">
            <span className="font-mono">output_path</span>
            <Tooltip
              content="Where to write the result. '$' = merge the whole outputs dict top-level. '$.foo' = write under item.data.foo (whole dict, or just the scalar value when 'output_field' is also set)."
              side="right"
            >
              <span tabIndex={0} className="ml-1 cursor-help text-muted-foreground/70 hover:text-foreground">
                <Info className="h-3 w-3" strokeWidth={1.5} />
              </span>
            </Tooltip>
          </Label>
          <Input
            id="cm-output-path"
            value={outputPath}
            onChange={(e) => {
              const next = e.target.value;
              // Switching back to "$" makes output_field meaningless
              // (and the backend validator would reject the combo);
              // drop it so the form stays valid.
              if (next === "$" && outputField) {
                onChange({
                  ...config,
                  output_path: next,
                  output_field: null,
                });
              } else {
                updateScalar("output_path", next);
              }
            }}
            placeholder="$"
            className="font-mono text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cm-on-miss" className="text-xs">
            <span className="font-mono">on_miss</span>
            <Tooltip
              content="fail = raise (default). pass_through = emit item unchanged. default = merge default_values."
              side="right"
            >
              <span tabIndex={0} className="ml-1 cursor-help text-muted-foreground/70 hover:text-foreground">
                <Info className="h-3 w-3" strokeWidth={1.5} />
              </span>
            </Tooltip>
          </Label>
          <select
            id="cm-on-miss"
            className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
            value={onMiss}
            onChange={(e) => updateScalar("on_miss", e.target.value)}
          >
            <option value="fail">fail (raise)</option>
            <option value="pass_through">pass_through (emit unchanged)</option>
            <option value="default">default (merge default_values)</option>
          </select>
        </div>
      </div>

      {/* output_field — pick a single output to write as a scalar.
          Only meaningful when output_path is a nested key; hidden
          on the default "$" merge to avoid ambiguity. */}
      {outputPath !== "$" && declaredOutputs.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="cm-output-field" className="text-xs">
            <span className="font-mono">output_field</span>
            <span className="ml-1 text-[10px] text-muted-foreground">
              optional
            </span>
            <Tooltip
              content="Pick a single output by name to write as a SCALAR at output_path. Leave empty to write the whole outputs dict. Useful to replace an existing field (like customer_external_code) with the resolved canonical id in one step."
              side="right"
            >
              <span
                tabIndex={0}
                className="ml-1 cursor-help text-muted-foreground/70 hover:text-foreground"
              >
                <Info className="h-3 w-3" strokeWidth={1.5} />
              </span>
            </Tooltip>
          </Label>
          <select
            id="cm-output-field"
            className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs font-mono"
            value={outputField ?? ""}
            onChange={(e) =>
              updateScalar("output_field", e.target.value || null)
            }
          >
            <option value="">— write the whole outputs dict —</option>
            {declaredOutputs.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name} ({f.type})
              </option>
            ))}
          </select>
          {outputField && (
            <p className="text-[10px] text-muted-foreground">
              <code className="font-mono">item.data{outputPath.replace("$", "")}</code>{" "}
              will be set to the scalar value of{" "}
              <code className="font-mono">{outputField}</code>.
            </p>
          )}
        </div>
      )}

      {onMiss === "default" && (
        <div className="space-y-1.5">
          <Label className="text-xs">
            <span className="font-mono">default_values</span>
            <span className="ml-1 text-[10px] text-muted-foreground">
              JSON object — keys should match declared outputs
            </span>
          </Label>
          <div className="h-32 w-full">
            <CodeEditor
              value={JSON.stringify(defaultValues, null, 2)}
              onChange={(next) => {
                if (next == null) {
                  updateScalar("default_values", {});
                  return;
                }
                try {
                  updateScalar("default_values", JSON.parse(next));
                } catch {
                  // Keep the raw text in flight — save will fail with
                  // a useful pydantic error if it's still invalid.
                  // We don't surface the parse error here to avoid
                  // flicker on each keystroke.
                }
              }}
              language="json"
              height="100%"
              inline
            />
          </div>
        </div>
      )}
    </div>
  );
}
